import React, { useState, useEffect } from 'react'
import { useAuth } from "./api/authStore"
import moment from 'moment'
import dayjs from 'dayjs'
import { HistoryDesktopDateRangePicker } from './HistoryDateRangePickers'
import PaginationActions from './components/PaginationActions'

const getStatusText = (status) => {
    switch (status) {
        case 'pending':
            return 'Pending'
        case 'reject':
        case 'rejected':
            return 'Rejected'
        case 'refund':
        case 'refunded':
        default:
            return 'Refunded'
    }
}

const StatusChip = ({ status }) => (
    <span className="shop-type-chip">{getStatusText(status)}</span>
)

const TicketsTable = (props) => {
    const { user, getAccessTokenSilently, setToken, logout } = useAuth()

    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)
    const [ticketsList, setTicketsList] = React.useState([])
    const [totalTickets, setTotalTickets] = React.useState(0)
    const [selectedTicket, setSelectedTicket] = React.useState(0)

    const [startDate, setStartDate] = React.useState(() => dayjs())
    const [endDate, setEndDate] = React.useState(() => dayjs().endOf('month'))

    // CSS стили для толстой полосы прокрутки
    React.useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            /* Адаптивная полоса прокрутки для таблицы TicketsTable */
            .tickets-table-container::-webkit-scrollbar {
                height: 24px; /* В 3 раза толще стандартной */
            }
            
            .tickets-table-container::-webkit-scrollbar-track {
                background: var(--dark-gray);
                border-radius: 12px;
            }
            
            /* Светлая тема - оранжевая полоса */
            [data-theme="light"] .tickets-table-container::-webkit-scrollbar-thumb {
                background: var(--primary-orange);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="light"] .tickets-table-container::-webkit-scrollbar-thumb:hover {
                background: var(--secondary-orange);
            }
            
            /* Темная тема - светлая серая полоса */
            [data-theme="dark"] .tickets-table-container::-webkit-scrollbar-thumb,
            :root:not([data-theme="light"]) .tickets-table-container::-webkit-scrollbar-thumb {
                background: var(--light-gray);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="dark"] .tickets-table-container::-webkit-scrollbar-thumb:hover,
            :root:not([data-theme="light"]) .tickets-table-container::-webkit-scrollbar-thumb:hover {
                background: #B0B0B0;
            }
            
            /* Для Firefox - светлая тема */
            [data-theme="light"] .tickets-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--primary-orange) var(--dark-gray);
            }
            
            /* Для Firefox - темная тема */
            [data-theme="dark"] .tickets-table-container,
            :root:not([data-theme="light"]) .tickets-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--light-gray) var(--dark-gray);
            }
        `
        document.head.appendChild(style)
        
        return () => {
            document.head.removeChild(style)
        }
    }, [])

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - ticketsList?.length) : 0

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    useEffect(()=>{
      const callAsync = async() => {
          try {
            const dateStartFormated = startDate.format('YYYY-MM-DD')
            const dateEndFormated = endDate.format('YYYY-MM-DD')

            // Получаем токен один раз
            const token = await getAccessTokenSilently()

            const ticketsResponse = await props.apiClient.setToken(token).getUserTickets(dateStartFormated, dateEndFormated, page, rowsPerPage)
            setTicketsList(ticketsResponse?.refunds || [])
            setTotalTickets(ticketsResponse?.total || 0)
          } catch (e) {
          }
      }
      callAsync()   
    },[startDate, endDate, page, rowsPerPage, getAccessTokenSilently])

    const handleDateRangeChange = React.useCallback((start, end) => {
      setStartDate(start)
      setEndDate(end)
    }, [])

    const getTicketsDefaultRange = React.useCallback(() => ({
      start: dayjs().startOf('day'),
      end: dayjs().endOf('month')
    }), [])

    const rowClick = (row) => {
      // console.log(row.id)
      setSelectedTicket(row)
    }

    return (
        <div>
            <h1 className="shop-page-title" style={{ 
                marginTop: '8px',
                marginBottom: '8px',
                marginLeft: '0px',
                fontSize: 'var(--text-2xl)',
            }}>
                Tickets
            </h1>
            
            <div className="shop-filters-bar" style={{
                marginBottom: 'var(--spacing-sm)',
                display: 'flex',
                gap: 'var(--spacing-lg)',
                alignItems: 'flex-end',
                flexWrap: 'wrap',
            }}>
                <HistoryDesktopDateRangePicker
                    label="Date range"
                    startDate={startDate}
                    endDate={endDate}
                    onRangeChange={handleDateRangeChange}
                    getResetRange={getTicketsDefaultRange}
                />
            </div>
            
            {ticketsList?.length > 0 ? (
                <div className="table-container tickets-table-container" style={{ 
                    marginTop: 'var(--spacing-sm)',
                    overflowX: 'auto' 
                }}>
                    <table className="table table-proxies" style={{ minWidth: '1000px' }}>
                            <thead>
                                <tr>
                                    <th>Creation Date</th>
                                    <th>Status</th>
                                    <th>Proxy ID</th>
                                    <th>Refund Reason</th>
                                    <th>Description</th>
                                    <th>Close Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ticketsList?.map((row, index) => (
                                    <tr 
                                        key={index}
                                      onClick={() => rowClick(row)}
                                        style={{
                                            backgroundColor: selectedTicket?.refundid === row.refundid ? 'var(--shop-row-selected-bg)' : 'transparent',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <td>{moment(row?.createdAt).format('L LT')}</td>
                                        <td>
                                            <StatusChip status={row?.status} />
                                        </td>
                                        <td>{row?.rental?.rentalid}</td>
                                        <td>{row?.reason}</td>
                                        <td>{row?.message}</td>
                                        <td>{row?.status === 'pending' ? '' : moment(row?.updatedAt).format('L LT')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                    <div className="shop-table-footer" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 'var(--spacing-lg)',
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 'var(--spacing-md)' 
                        }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                                Rows per page:
                            </span>
                            <select 
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                style={{
                                    borderRadius: 'var(--radius-sm)',
                                    padding: '8px 12px',
                                    fontSize: 'var(--text-sm)'
                                }}
                            >
                                {[10, 20, 30, 40, 50].map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>

                        <PaginationActions 
                            page={page}
                            rowsPerPage={rowsPerPage}
                            count={totalTickets}
                            onPageChange={handleChangePage}
                            centerLabel="range"
                        />
                    </div>
                </div>
            ) : (
                <div className="empty-state">
                    <div className="empty-state-icon">🎫</div>
                    <h3 className="empty-state-title">No tickets found</h3>
                    <p className="empty-state-description">
                        You don't have any tickets for the selected period.
                    </p>
            </div>
            )}
        </div>
    )
}

export default TicketsTable